import { Component, OnInit, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { KnowledgeApiService, KnowledgeSourceRecord } from './knowledge-api.service';

@Component({
  standalone: true,
  selector: 'ain-knowledge-sources',
  imports: [AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule, InputTextModule, TextareaModule, RouterLink],
  template: `
    <ain-page [title]="t('knowledge.sources.title')" [description]="t('knowledge.sources.description')">
      <section class="create-box">
        <h3>{{ t('knowledge.sources.createTitle') }}</h3>
        <div class="grid">
          <label>
            <span>{{ t('knowledge.sources.fields.sourceType') }}</span>
            <input pInputText [(ngModel)]="createForm.sourceType" />
          </label>
          <label>
            <span>{{ t('knowledge.sources.fields.configJson') }}</span>
            <textarea pTextarea rows="2" [(ngModel)]="createForm.configJson"></textarea>
          </label>
          <label>
            <span>{{ t('knowledge.sources.fields.accessScopeJson') }}</span>
            <textarea pTextarea rows="2" [(ngModel)]="createForm.accessScopeJson"></textarea>
          </label>
        </div>
        <button pButton type="button" [label]="t('common.create')" [disabled]="creating()" (click)="create()"></button>
      </section>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.loadFailed')" [message]="error() ?? ''" />
      } @else if (!rows().length) {
        <ain-state type="empty" [title]="t('common.noRecords')" />
      } @else {
        <p-table [value]="rows()" [paginator]="true" [rows]="15" dataKey="id">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('knowledge.sources.fields.sourceType') }}</th>
              <th>{{ t('columns.status') }}</th>
              <th>{{ t('knowledge.sources.fields.lastIngested') }}</th>
              <th></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.sourceType }}</td>
              <td>{{ row.status }}</td>
              <td>{{ row.lastIngestedUtc || t('common.notAvailable') }}</td>
              <td><a [routerLink]="['/knowledge/sources', row.id]">{{ t('common.open') }}</a></td>
            </tr>
          </ng-template>
        </p-table>
      }
    </ain-page>
  `,
  styles: [`
    .create-box { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; margin-bottom: 16px; background: #f8fafc; }
    .grid { display: grid; gap: 10px; margin-bottom: 10px; }
    label { display: grid; gap: 6px; }
    a { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
  `]
})
export class KnowledgeSourcesComponent implements OnInit {
  private readonly api = inject(KnowledgeApiService);
  private readonly localization = inject(LocalizationService);

  readonly rows = signal<KnowledgeSourceRecord[]>([]);
  readonly loading = signal(true);
  readonly creating = signal(false);
  readonly error = signal<string | null>(null);

  createForm = {
    sourceType: 'ModuleData',
    configJson: '{}',
    accessScopeJson: '{}'
  };

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.listSources().subscribe({
      next: rows => { this.rows.set(rows); this.loading.set(false); },
      error: err => { this.error.set(err?.message ?? 'load failed'); this.loading.set(false); }
    });
  }

  create(): void {
    this.creating.set(true);
    this.api.createSource(this.createForm).subscribe({
      next: () => { this.creating.set(false); this.load(); },
      error: () => this.creating.set(false)
    });
  }

  t(key: string): string { return this.localization.translate(key); }
}
